import type {
  PlatformProductQueryResponse,
  PlatformProductQueryVariables,
} from '~/api/graphql/queries/platformProduct.generated';
import type { IPlatformProductDetail } from '../utils';

import { PlatformProductDocument } from '~/api/graphql/queries/platformProduct.generated';
import { PLATFORM_PRODUCT_DETAILS_QUERY_KEY } from '../constants';
import { STORE_PLATFORM } from '~/constants/storePlatform';
import { captureSentryError } from '~/exceptions/sentry';
import { useQuery } from '@tanstack/react-query';
import { appFetcher } from '~/api/utils';
import { useCallback } from 'react';

export interface IPlatformProductDetails {
  products?: IPlatformProductDetail[];
  isLoadingProducts: boolean;
  /** Re-reads the products from Shopify — what a "sync price" action asks for. Rejects if it fails. */
  handleRefreshProducts: () => Promise<void>;
}

const fetchPlatformProduct = (baseID: string): Promise<PlatformProductQueryResponse> =>
  appFetcher<PlatformProductQueryResponse, PlatformProductQueryVariables>(PlatformProductDocument, {
    baseID,
    platform: STORE_PLATFORM.SHOPIFY,
  })();

// allSettled, not all: a product that can't be read — deleted after the campaign ended, or throttled —
// must only cost its own card the price and thumbnail, the other card keeps what came back for it.
// A rejection is surfaced as a deleted sentinel rather than dropped, so the row can still render one
// instead of falling back to its stale picked-product title forever.
const fetchPlatformProducts = async (baseIDs: string[]): Promise<IPlatformProductDetail[]> => {
  const responses = await Promise.allSettled(baseIDs.map(fetchPlatformProduct));

  return responses.map((response, index) => {
    if (response.status === 'fulfilled') return response.value.platformProduct;

    captureSentryError(response.reason, {
      source: 'usePlatformProductDetails',
      level: 'warning',
      tags: { experiment: 'Experiment' },
      extra: { baseID: baseIDs[index] },
    });

    // Cast: every other field is unknown once the read failed, and callers only need isDeleted.
    return { baseID: baseIDs[index], isDeleted: true } as IPlatformProductDetail;
  });
};

/**
 * Reads each product with its variants. platformProduct is the only query serving variants —
 * platformProducts leaves them null — so the products are read one by one, in parallel.
 */
export const usePlatformProductDetails = (baseIDs: string[]): IPlatformProductDetails => {
  // isFetching, not isLoading: a disabled query still reports isLoading in react-query v4.
  const { data, isFetching, refetch } = useQuery({
    queryKey: [PLATFORM_PRODUCT_DETAILS_QUERY_KEY, baseIDs],
    queryFn: () => fetchPlatformProducts(baseIDs),
    enabled: baseIDs.length > 0,
    // Picking a product rewrites baseIDs, hence the key: the previous data keeps the untouched card
    // from dropping its thumbnail and price while the new set is in flight.
    keepPreviousData: true,
    // 'always' keeps prices honest after an edit in Shopify admin; plain `true` never fires because
    // the global staleTime in app/root.tsx is 7 days.
    refetchOnWindowFocus: 'always',
  });

  // `throwOnError` so a caller that reports the outcome sees a failure; refetch resolves with the
  // error tucked into its result otherwise, and a broken sync would read as a successful one.
  const handleRefreshProducts = useCallback(async () => {
    await refetch({ throwOnError: true });
  }, [refetch]);

  return { products: data, isLoadingProducts: isFetching, handleRefreshProducts };
};
